import type { DataCategory } from './types'

export interface DataUiSliceState {
  dataCategory: DataCategory
  selectedEntityId: string | null
  dataInspectorOpen: boolean
  entityTreeExpanded: Record<string, boolean>
  searchQuery: string
}

/**
 * The slice's starting position, as data.
 *
 * ONE source for two consumers: the factory below spreads it, and
 * `project-session-exit.ts` re-applies it when a project is left. Every field
 * of this slice is one project's data-navigator position — which category is
 * open, which row is selected, which tree nodes are unfolded, what is typed
 * in the filter — so the whole slice is project-scoped and the exit path
 * takes the WHOLE object rather than naming keys. A field added here is
 * cleared on project exit without anyone having to remember it; that is the
 * point of the shape (slice-5A §A5-a found `selectedEntityId` pinned to a
 * closed project's variable and shown on the dashboard).
 */
export const INITIAL_DATA_UI_STATE: DataUiSliceState = {
  dataCategory: 'variables',
  selectedEntityId: null,
  dataInspectorOpen: false,
  entityTreeExpanded: {},
  searchQuery: '',
}

/**
 * A fresh copy of the starting position. `entityTreeExpanded` is a mutable
 * record, so handing out the module-level object itself would let one store
 * (or one test) write through into every later reset.
 *
 * `structuredClone`, not a spread with `entityTreeExpanded: {}` beside it:
 * restating one field by name is how the NEXT mutable field added above ends
 * up shared by reference, which is the same forget-one-key failure this whole
 * lane exists to remove.
 */
export function freshDataUiState(): DataUiSliceState {
  return structuredClone(INITIAL_DATA_UI_STATE)
}

export interface DataUiSlice extends DataUiSliceState {
  setDataCategory: (category: DataCategory) => void
  selectEntity: (id: string | null) => void
  setDataInspectorOpen: (open: boolean) => void
  toggleTreeNode: (id: string) => void
  setSearchQuery: (query: string) => void
  expandAllNodes: (blocks: string[]) => void
  collapseAllNodes: () => void
}

type Set = (fn: (state: DataUiSlice) => void) => void

export function createDataUiSlice(
  set: Set,
  _get: unknown,
  _api: unknown,
): DataUiSlice {
  return {
    // Spread, never restated — see INITIAL_DATA_UI_STATE's header for why the
    // initial position and the project-exit reset must be the same object.
    ...freshDataUiState(),
    setDataCategory: (category) => set((s) => { s.dataCategory = category }),
    selectEntity: (id) => set((s) => {
      s.selectedEntityId = id
      s.dataInspectorOpen = id !== null
    }),
    setDataInspectorOpen: (open) => set((s) => { s.dataInspectorOpen = open }),
    toggleTreeNode: (id) => set((s) => {
      s.entityTreeExpanded[id] = !s.entityTreeExpanded[id]
    }),
    setSearchQuery: (query) => set((s) => { s.searchQuery = query }),
    expandAllNodes: (blocks) => set((s) => {
      const expanded: Record<string, boolean> = {}
      for (const block of blocks) {
        expanded[block] = true
      }
      expanded['global-flows'] = true
      s.entityTreeExpanded = expanded
    }),
    collapseAllNodes: () => set((s) => { s.entityTreeExpanded = {} }),
  }
}
